import {Box, Grid, List, ListItem, makeStyles, Paper, Typography, useMediaQuery} from '@material-ui/core';
import {ContentMetaData} from 'Communication/api';
import {getLegalNoticeData} from 'Core/api/legalNoticeApi';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ScrollToTop from 'Shared/components/ScrollToTop';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import { themeColors } from 'Theme';

const useStylesLegalPage = makeStyles((theme: any) => ({
  legal: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    zIndex: 5,
    flexDirection: 'column',
    margin: '1rem 4rem 2rem 4rem',
    [theme.breakpoints.down('sm')]: {
      margin: '3.5rem .5rem 2rem .5rem',
    },
  },

  legal_grid: {
    display: 'flex',
    width: '100%',
    flexGrow: 1,
  },

  legal__header: {
    display: 'flex',
    alignSelf: 'flex-start',
    marginLeft: '1rem',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    wordBreak: 'break-word',
  },

  legal__title: {
    color: themeColors.blue_600,
    fontFamily: 'inherit',
    fontSize: '2.625rem',
    fontWeight: 300,
    letterSpacing: '-0.0125rem',
    textTransform: 'capitalize',
    lineHeight: '1.2',
    margin: '0.65rem 0px 0.65rem',

    [theme.breakpoints.down('xs')]: {
      fontSize: '1.85rem',
    },
  },

  legal__title_blue: {
    color: 'rgb(69, 188, 229)',
    fontStyle: 'normal',
  },

  legal_nav__icon: {
    background: 'none',
    border: 'none',
    borderRadius: '0.5rem',
    transform: 'rotate(90deg)',
    margin: '0px',
    transition: 'background-color 0.2s ease 0s, border-color 0.2s ease 0s, box-shadow 0.2s ease 0s, color 0.2s ease 0s',

    '&:hover': {
      background: 'rgb(69, 188, 229)',
    },

    '&:active': {
      background: 'rgb(69, 188, 229)',
      boxShadow: 'rgb(0 0 0 / 20%) 0.35rem 0px 0.35rem 0px inset',
    },

    '&:active svg': {
      fill: 'rgb(69, 188, 229)',
    },

    '&:focus svg': {
      fill: 'rgb(69, 188, 229)',
    },

    '&:focus': {
      background: 'rgb(255, 255, 255)',
      boxShadow:
        'rgb(255 255 255) 0px 0px 0px 0.15rem, rgb(69 188 229) 0px 0px 0px 0.5rem, rgb(69 188 229) 0px 0px 0px 0.125rem inset',
      outline: '0px',
    },

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  content_text: {
    padding: '2rem',
    display: 'flex',
    flexGrow: 1,
    width: '100%',
    wordBreak: 'break-word',

    '& a': {
      color: themeColors.blue_400,
    },
    '& h2': {
      fontWeight: 400,
    },
  },

  legal_content__text: {
    maxWidth: '70%',
    minWidth: '70%',
    padding: '0 1rem',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      minWidth: '100%',
    },
  },

  legal_links__list: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  svgclass: {
    fill: '#fff',

    '&:active': {
      fill: 'rgb(69, 188, 229)',
    },

    '&:focus': {
      fill: 'rgb(69, 188, 229)',
    },
  },

  menu_shown: {
    display: 'block',
    zIndex: 100,
    position: 'absolute',
    width: '100%',
    top: '9.65rem',
    boxShadow: 'rgb(0 0 0 / 10%) 0px 0.5rem 1rem',

    [theme.breakpoints.up('sm')]: {
      top: '9rem',
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  menu_hidden: {
    display: 'none',
  },
}));

const useStylesSideNav = makeStyles((theme: any) => ({
  legal_nav__li: {
    borderLeft: '5px solid transparent',

    '&:hover': {
      borderLeft: `5px solid ${themeColors.blue_400}`,
      background: themeColors.brightBlue_100,
    },

    [theme.breakpoints.down('sm')]: {
      borderTop: '1px solid rgb(227, 233, 237)',
    },

    '& a': {
      color: themeColors.blue_400,
      display: 'block',
      fontWeight: 400,
      padding: '.5rem',
      textDecoration: 'none',
      width: '100%',

      '&:hover': {
        background: themeColors.brightBlue_100,
      },
    },
  },

  legal_nav__li_active: {
    borderLeft: `5px solid ${themeColors.blue_400}`,
  },
  legal_nav__close: {
    background: 'none',
    border: 'none',
    borderRadius: '1rem',
    padding: '0.5rem',
    transition: 'background-color 0.2s ease 0s, border-color 0.2s ease 0s, box-shadow 0.2s ease 0s, color 0.2s ease 0s',
    fontSize: '.95rem',
    marginRight: '1rem',

    '&:hover': {
      background: 'rgb(69, 188, 229)',
      color: 'rgb(255, 255, 255)',
    },

    '&:active': {
      background: 'rgb(69, 188, 229)',
      boxShadow: 'rgb(0 0 0 / 20%) 0.35rem 0px 0.35rem 0px inset',
    },

    '&:focus': {
      background: 'rgb(255, 255, 255)',
      color: 'rgb(69, 188, 229)',
      boxShadow:
        'rgb(255 255 255) 0px 0px 0px 0.15rem, rgb(69 188 229) 0px 0px 0px 0.5rem, rgb(69 188 229) 0px 0px 0px 0.125rem inset',
      outline: '0px',
    },
  },

  legal_nav__title: {
    fontWeight: 400,
    fontSize: '1.25rem',
    color: themeColors.blue_600,
  },

  legal_nav__header: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'rgb(47, 56, 71)',
    margin: '0 1rem',

    [theme.breakpoints.up('xs')]: {
      margin: '0',
      paddingLeft: '1.75rem',
    },
  },
}));

const PopupMenuIcon = (props: any) => (
  <button className={props.cssClass} onClick={props.handleMenu}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      focusable="false"
      className={props.svgclass}
      width="32px"
      height="32px"
      viewBox="0 0 64 64"
    >
      <title />
      <path d="M32 25c-3.843 0-6.957 3.114-6.957 6.957s3.114 6.957 6.957 6.957 6.957-3.114 6.957-6.957S35.843 25 32 25zM6.957 25C3.114 25 0 28.114 0 31.957s3.114 6.957 6.957 6.957 6.957-3.114 6.957-6.957S10.8 25 6.957 25zm50.087 0c-3.843 0-6.957 3.114-6.957 6.957s3.114 6.957 6.957 6.957 6.957-3.114 6.957-6.957S60.887 25 57.044 25z" />
    </svg>
  </button>
);

export const SideNav = (props: any) => {
  const classes = useStylesSideNav();

  return (
    <Paper className={props.cssClass} style={{borderRadius: 0}}>
      <div className={classes.legal_nav__header}>
        <h2 className={classes.legal_nav__title}>
          legal notices
        </h2>
        {props.forPopup === true ? (
          <button className={classes.legal_nav__close} onClick={props.handleMenu}>
            close
          </button>
        ) : null}
      </div>

      <List component="nav" aria-label="List of Agreements" style={{paddingTop: '0', paddingBottom: '0'}}>
        {Array.isArray(props.legalLinks) &&
          props.legalLinks.map((link: any) => (
            <ListItem
              className={`${link.contentMetadataId === props.activeDocId ? classes.legal_nav__li_active : ''} ${
                classes.legal_nav__li
              }`}
              key={`${link.contentMetadataId}`}
              id={`${link.contentMetadataId}`}
            >
              {link.externalLinkFlag ? (
                <a href={`${link.externalURL}`} target="_blank" rel="noopener noreferrer" onClick={props.handleMenu}>
                  {link.linkTitle}
                </a>
              ) : (
                <Link
                  to={`/legal-notices/${toKebabCase(link.linkTitle !== undefined ? link.linkTitle : '')}?id=${
                    link.contentMetadataId
                  }`}
                  onClick={props.handleMenu}
                >
                  {link.linkTitle}
                </Link>
              )}
            </ListItem>
          ))}
      </List>
    </Paper>
  );
};

const LegalNotice = () => {
  const [selectedNotice, setSelectedNotice] = useState<ContentMetaData>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const {footerContent} = useSelector((state: ReducerState) => state.footer);
  const isMobileView = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const classes = useStylesLegalPage();
  const location = useLocation();

  const handleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const getFooterContentList = () => {
    switch (footerContent?.key) {
      case 'ServerError':
      case 'Unauthorized':
      case 'Conflict':
      case 'NotFound':
        return footerContent.message;

      case 'Loaded':
        return footerContent.Data.contentMetaDataList ? footerContent.Data.contentMetaDataList : [];

      case 'Empty':
      case 'Loading':
      default:
        return [];
    }
  };

  useEffect(() => {
    getLegalNoticeData().then(legalNotice => {
      if (legalNotice !== undefined) {
        setSelectedNotice(legalNotice);
      }
    });
  }, [location]);

  return (
    <>
      <SideNav
        handleMenu={handleMenu}
        cssClass={menuOpen === true ? classes.menu_shown : classes.menu_hidden}
        legalLinks={getFooterContentList()}
        activeDocId={selectedNotice.contentMetadataId}
        forPopup={true}
      />
      <div className={classes.legal}>
        <Grid container spacing={2} className={classes.legal_grid}>
          <Box className={classes.legal__header}>
            <h1 className={classes.legal__title}>
              {selectedNotice.linkTitle}
            </h1>
            <PopupMenuIcon cssClass={classes.legal_nav__icon} svgclass={classes.svgclass} handleMenu={handleMenu} />
          </Box>
          <Grid item className={classes.legal_content__text}>
            <Paper className={classes.content_text} style={{borderRadius: 0, minHeight: '30rem'}}>
              <Typography
                dangerouslySetInnerHTML={{
                  __html: selectedNotice.contentText !== undefined ? selectedNotice.contentText : '',
                }}
              />
            </Paper>
            {isMobileView && <ScrollToTop overrideStyles={{bottom: '60px'}}/>}
          </Grid>
          <Grid item style={{maxWidth: '30%', width: '30%', zIndex: 5}}>
            <SideNav
              legalLinks={getFooterContentList()}
              cssClass={classes.legal_links__list}
              forPopup={false}
              activeDocId={selectedNotice.contentMetadataId}
            />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default LegalNotice;
